/**
 * Tells a keyboard shortcut whether some OTHER component genuinely claimed the
 * key, as opposed to Cornerstone reflexively cancelling it.
 *
 * ## The bug this exists to fix
 *
 * Cornerstone gives every enabled viewport `tabIndex = -1`
 * (`ContextPoolRenderingEngine.js:57`) and binds its own keydown listener to
 * that element (`tools/store/addEnabledElement.js:14` ->
 * `eventListeners/keyboard/index.js:4`). That listener calls a bare
 * `evt.preventDefault()` with no check of whether anything handled the key
 * (`keyDownListener.js:25`).
 *
 * A `tabindex="-1"` element takes focus when clicked. So the moment a
 * radiologist clicks an image - the normal reading state - every subsequent
 * keystroke reaches our window-level handlers already `defaultPrevented`, and
 * a handler guarding on `if (e.defaultPrevented) return` silently does
 * nothing. Clicking anything outside the canvas moves focus off the viewport
 * and everything works again, which is exactly how it was reported: "only
 * works if you click out of the canvas, which is an extra click".
 *
 * It is not one shortcut. Of the 38 configurable bindings, the 28 bare-key
 * ones are dead, on every surface that mounts a Cornerstone element (2D
 * viewer, MPR, volume rendering, PET/CT fusion). Video and the loupe are
 * unaffected - no enabled element.
 *
 * The listener is one-shot: it removes itself after the first keydown and
 * re-arms on the next document keyup. That is what makes the symptom read as
 * a quirk rather than a broken feature:
 *   - an isolated tap is eaten EVERY time, because the keyup re-arms it
 *     before the next press. Tapping Space is therefore dead 100% of the time;
 *   - a HELD key is eaten once and then auto-repeat gets through, so holding
 *     Space makes the crosshairs flicker;
 *   - a modifier chord survives, because the modifier's own keydown spends
 *     the one preventDefault before the real key arrives.
 *
 * Which is why the ten chord bindings kept working and nobody filed this as
 * "keyboard shortcuts are broken".
 *
 * ## Why we cannot simply drop the guard
 *
 * The guard is load-bearing. Radix components call `preventDefault()` WITHOUT
 * `stopPropagation()`, so the event still reaches window:
 *   - a slider thumb (the cine trim rail) owns ArrowLeft/ArrowRight,
 *   - a dialog owns Escape.
 * Without the guard, ArrowRight would nudge the thumb AND step a viewport, and
 * Escape would close a dialog AND tear down the MPR session.
 *
 * ## Why we cannot key off WHERE the event came from
 *
 * The obvious fix - "ignore defaultPrevented when the target is a viewport" -
 * is wrong, and wrong in a way that bites inside the very component being
 * fixed. Radix tooltips open on HOVER and never move focus. Click a pane, move
 * the mouse to the MPR toolbar, let a tooltip appear, press Escape: Radix
 * cancels the event from a document-level capture listener, but `e.target` is
 * still the pane, because focus never left it. A target-based exemption would
 * fire and close the whole MPR session.
 *
 * Where an event came from is not evidence about who consumed it.
 *
 * ## What actually separates them: WHEN
 *
 * The three sources of `preventDefault` are cleanly separated by phase:
 *   - Radix dismissable layers (dialog, dropdown, popover, tooltip):
 *     `document`, CAPTURE phase.
 *   - React's delegated onKeyDown (Radix Slider arrows): the React root
 *     container, bubble phase.
 *   - Cornerstone: the viewport ELEMENT itself, target/bubble phase.
 *
 * A capture-phase listener on `document.body` sits strictly after the first
 * and strictly before the other two. At that instant:
 *   - `defaultPrevented === true`  -> a real layered component claimed the key
 *     with intent. Respect it.
 *   - `defaultPrevented === false` AND the event targets an enabled viewport
 *     -> any preventDefault that follows can only be Cornerstone's blanket
 *     call, so it carries no meaning and must not suppress the shortcut.
 *
 * We record the second case and consult it instead of reading
 * `defaultPrevented` raw.
 */
/**
 * Events whose only `preventDefault` can be Cornerstone's.
 *
 * A WeakSet keyed on the event object: entries die with the event, so this
 * never grows and never needs clearing.
 */
const cornerstoneOwned = new WeakSet();
/**
 * True when the event targets a Cornerstone enabled element.
 *
 * `data-viewport-uid` is set in `Viewport.js:83` and removed on disable, and
 * is the same attribute Cornerstone's own `getEnabledElement()` reads, so it
 * is the authoritative signal. Using the attribute rather than
 * `getEnabledElements()` is deliberate: that API returns objects with no
 * `element` field, can contain `undefined` entries mid-teardown, and walks
 * DICOM metadata for stack viewports - which would run on every keystroke,
 * including auto-repeat, to answer a question the DOM answers in O(1).
 *
 * Matched EXACTLY, not via `closest()`. Cornerstone injects only a canvas and
 * a pointer-events:none SVG layer into the element, and our panes render every
 * badge and button as a SIBLING, so a real viewport keydown always targets the
 * element itself. An exact match means an overlay control that happens to sit
 * inside a viewport is never mistaken for Cornerstone.
 */
function targetsEnabledViewport(target) {
    return target instanceof HTMLElement && target.hasAttribute('data-viewport-uid');
}
/**
 * Start recording key provenance. Call once, at app start.
 *
 * MUST be `document.body` in the CAPTURE phase. On `window` or `document` it
 * would run BEFORE Radix's document-capture handlers and see
 * `defaultPrevented === false` for a dialog's Escape, which is precisely the
 * case the guard has to keep catching.
 *
 * Returns a teardown function.
 */
export function installViewportKeyProvenance() {
    const mark = (e) => {
        // A layered component already claimed this key, before Cornerstone got a
        // chance. Leave it alone.
        if (e.defaultPrevented)
            return;
        if (targetsEnabledViewport(e.target))
            cornerstoneOwned.add(e);
    };
    document.body.addEventListener('keydown', mark, true);
    return () => document.body.removeEventListener('keydown', mark, true);
}
/**
 * The single replacement for `if (e.defaultPrevented) return`.
 *
 * Returns true only when something OTHER than Cornerstone consumed the key.
 *
 * Degrades safely: if provenance was never installed the WeakSet is empty,
 * this collapses to a plain `defaultPrevented` check, and behaviour is exactly
 * what it was before this module existed.
 */
export function isKeyClaimedElsewhere(e) {
    return e.defaultPrevented && !cornerstoneOwned.has(e);
}
/**
 * Claim a key from a handler that runs AFTER Cornerstone: a bubble-phase
 * listener on `document`, such as a non-modal panel's Escape. By then a
 * focused viewport has already cancelled the event, so a plain
 * `preventDefault()` would leave it reading as Cornerstone's and the window
 * shortcuts (Escape is `nav.back`) would still act on it. This records it as
 * genuinely consumed, so every later `isKeyClaimedElsewhere` check skips it.
 */
export function claimKey(e) {
    cornerstoneOwned.delete(e);
    e.preventDefault();
}
